import * as yup from 'yup';
import { useState } from 'react';
import { Formik } from 'formik';
import { DropDown, ChipInput, ChipInputTagErrors } from '@qrios/frontend-library';

import { FORM_INPUT_ERROR_MSGS } from '../../../../../../constants/errors';
import DateRangePicker from '../../../../../common/fields/date-range-picker';
import { billingEndDateValidation, msisdnValidationSimple } from '../../../../../../constants/validation';
import { WithFilterFormLayout, FilterFormProps } from '../../../../../common/filters';
import { FILTERS, BILLING_STATUSES, PAGE_SIZES } from '../../../../../common/filters/constants';

import locales from '../locales';
import { CustomerAuthenticationBillingFilters } from './interfaces';
import {
  activeFiltersCountMapper,
  initFilters,
  CUSTOMER_AUTHENTICATION_PROVIDERS,
  CUSTOMER_AUTHENTICATION_METHODS
} from './filters';

const validationSchema = yup.object().shape({
  from: yup.date().required().typeError(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('from date')),
  to: billingEndDateValidation,
  customer_msisdn: yup.array(msisdnValidationSimple)
});

const FilterForm = ({ onSubmit, onClose, activeFilters }: FilterFormProps<CustomerAuthenticationBillingFilters>) => {
  const initialFiltersDifferentThanActiveFilters = activeFiltersCountMapper(activeFilters) !== 0;
  const [filtersChanged, setFilterChanged] = useState<boolean>(initialFiltersDifferentThanActiveFilters);
  const [initialValues, setInitialValues] = useState<CustomerAuthenticationBillingFilters>({
    from: activeFilters.from,
    to: activeFilters.to,
    status: activeFilters.status,
    operation_id: activeFilters.operation_id,
    page_size: activeFilters.page_size,
    customer_msisdn: activeFilters.customer_msisdn,
    provider_name: activeFilters.provider_name,
    authentication_method: activeFilters.authentication_method
  });

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(filters: CustomerAuthenticationBillingFilters) => {
        onSubmit({
          ...filters
        });
      }}
    >
      {({ values, handleSubmit, setFieldValue, errors, touched, handleBlur }) => {
        const handleChange = (field: string, value: unknown) => {
          setFieldValue(field, value);
          setFilterChanged(true);
        };

        const closeOrClearFilters = () => {
          if (!filtersChanged) {
            onClose();
          }
          setInitialValues(initFilters);
          setFilterChanged(false);
        };
        return (
          <WithFilterFormLayout
            onSecondary={closeOrClearFilters}
            onSubmit={handleSubmit}
            filtersChanged={filtersChanged}
          >
            <DateRangePicker
              from={{ name: FILTERS.FROM, value: values.from, error: errors.from, touched: touched.from }}
              to={{ name: FILTERS.TO, value: values.to, error: errors.to, touched: touched.to }}
              handleFromChange={value => handleChange(FILTERS.FROM, value)}
              handleToChange={value => handleChange(FILTERS.TO, value)}
              handleBlur={handleBlur}
              variant="nowEnabled"
            />
            <DropDown
              label={locales.common.filters.statuses.label}
              value={values.status}
              options={BILLING_STATUSES}
              name={FILTERS.STATUS}
              handleChange={handleChange}
              handleBlur={handleBlur}
              multiple={true}
              placeholder={locales.common.filters.statuses.placeholder}
            />
            <ChipInput
              error={errors.operation_id?.[(errors.operation_id ?? []).length - 1]}
              label={locales.common.filters.operationIds.label}
              value={values.operation_id}
              name={FILTERS.OPERATION_ID}
              handleChange={handleChange}
              handleBlur={handleBlur}
              placeholder={locales.common.filters.operationIds.placeholder}
              tagErrors={errors.operation_id as ChipInputTagErrors}
            />
            <ChipInput
              error={errors.customer_msisdn?.[(errors.customer_msisdn ?? []).length - 1]}
              label={locales.common.filters.customer_msisdn.label}
              value={values.customer_msisdn}
              name={FILTERS.CUSTOMER_MSISDN}
              handleChange={handleChange}
              handleBlur={handleBlur}
              placeholder={locales.common.filters.customer_msisdn.placeholder}
              tagErrors={errors.customer_msisdn as ChipInputTagErrors}
            />
            <DropDown
              label={locales.common.filters.provider_name.label}
              value={values.provider_name}
              options={CUSTOMER_AUTHENTICATION_PROVIDERS}
              name={FILTERS.PROVIDER_NAME}
              handleChange={handleChange}
              handleBlur={handleBlur}
              multiple={true}
              placeholder={locales.common.filters.provider_name.placeholder}
            />
            <DropDown
              label={locales.common.filters.authentication_method.label}
              value={values.authentication_method}
              options={CUSTOMER_AUTHENTICATION_METHODS}
              name={FILTERS.AUTHENTICATION_METHOD}
              handleChange={handleChange}
              handleBlur={handleBlur}
              multiple={true}
              placeholder={locales.common.filters.authentication_method.placeholder}
            />
            <DropDown
              label={locales.common.filters.resultsNumber.label}
              value={values.page_size}
              options={PAGE_SIZES}
              name={FILTERS.PAGE_SIZE}
              handleChange={handleChange}
              handleBlur={handleBlur}
              disableClearable={true}
            />
          </WithFilterFormLayout>
        );
      }}
    </Formik>
  );
};

export default FilterForm;
